import { RequestOptions } from 'graphql-request';
import snakeCase from 'lodash/snakeCase';

import type {
  AccountDTO,
  CreateAccountDTO,
  LocationDTO,
  MembershipDTO,
  UserDTO,
  LocationStatus,
  BillDTO,
  BillStatus,
  ServiceAgreementDTO,
  UpdateAccountDTO,
  ListViewDTO,
  CreateListViewDTO,
  UpdateListViewDTO,
} from '@invenco/accounts-interface';

import { DEFAULT_PAGE_SIZE } from '@/constants';

import {
  BillsBoolExp,
  BillsOrderByExp,
  BillsQueryQuery,
  BillsQueryQueryVariables,
  OrderBy,
} from '../../graphql/generated/platform/graphql';
import { BaseGateway, QueryRequestOptions, TokenGetter } from '../BaseGateway';
import {
  GatewayArrayFilter,
  GatewayBooleanFilter,
  GatewayName,
  GatewayNumberFilter,
  GatewayQueryProps,
  PaginatedResponse,
  SortDirection,
} from '../types';
import { UserMembershipSorter } from './UserMembershipSorter';
import { billsQuery } from './gql/platform/bills';
import { graphqlOperatorMap } from './graphql';

export type AccountSortBy = 'createdAt' | 'name';
export type LocationSortBy = 'createdAt' | 'name';
export type BillSortBy = 'createdAt' | 'periodStart';

export type BillSortField =
  | 'id'
  | 'billNumber'
  | 'billName'
  | 'billTotal'
  | 'status'
  | 'periodStart'
  | 'periodEnd'
  | 'recalculatedAt'
  | 'createdAt'
  | 'accountName'
  | 'locationName';

export type QueryBillsProps = GatewayQueryProps<{
  sortKey: BillSortField;
  filters: {
    status: GatewayArrayFilter;
    accountName: GatewayArrayFilter;
    locationName: GatewayArrayFilter;
    billTotal: GatewayNumberFilter;
    hasHolds: GatewayBooleanFilter;
  };
}>;

function getBillRelationNameWhereClause(
  relation: 'account' | 'location',
  filter: GatewayArrayFilter,
): BillsBoolExp {
  const comparison = { [graphqlOperatorMap[filter.operator]]: filter.value };
  return relation === 'account'
    ? { account: { name: comparison } }
    : { location: { name: comparison } };
}

function getBillOrderByClause(key: BillSortField, direction: OrderBy): BillsOrderByExp {
  switch (key) {
    case 'accountName':
      return { account: { name: direction } };
    case 'locationName':
      return { location: { name: direction } };
    case 'billName':
      return { name: direction };
    default: {
      const clause: BillsOrderByExp = {};
      clause[key] = direction;
      return clause;
    }
  }
}

export class AccountsGateway extends BaseGateway {
  name: GatewayName = 'accounts';

  constructor(
    getAccessToken: TokenGetter,
    private readonly userMembershipSorter: UserMembershipSorter,
  ) {
    super(getAccessToken);
  }

  async getAccount(id: string, options?: QueryRequestOptions): Promise<AccountDTO> {
    const { data } = await this.httpGet(`/v1/accounts/${id}`, options);
    return data.account;
  }

  async getAccounts(
    query?: {
      take?: number;
      cursor?: string;
      search?: string;
      sortBy?: AccountSortBy;
      sortDirection?: SortDirection;
    },
    options?: QueryRequestOptions,
  ): Promise<PaginatedResponse<AccountDTO>> {
    const params = {
      take: query?.take,
      cursor: query?.cursor,
      q: query?.search,
      sortBy: query?.sortBy ? snakeCase(query.sortBy) : undefined,
      sortDirection: query?.sortDirection,
    };
    const { data } = await this.httpGet('/v1/accounts', { params, ...options });
    return { ...data, items: data.accounts };
  }

  async createAccount(account: CreateAccountDTO): Promise<AccountDTO> {
    const { data } = await this.httpPost<{ account: AccountDTO }>('/v1/accounts', {
      data: { account },
    });
    return data.account;
  }

  async updateAccount(
    accountId: Required<AccountDTO['id']>,
    account: UpdateAccountDTO,
  ): Promise<AccountDTO> {
    const { data } = await this.httpPatch<{ account: AccountDTO }>(`/v1/accounts/${accountId}`, {
      data: { account },
    });
    return data.account;
  }

  async enableBillHolds(accountId: string, reason: string): Promise<AccountDTO> {
    const { data } = await this.httpPost<{ account: AccountDTO }>(
      `/v1/accounts/${accountId}/enable_bill_holds`,
      { data: { reason } },
    );
    return data.account;
  }

  async disableBillHolds(accountId: string): Promise<AccountDTO> {
    const { data } = await this.httpPost<{ account: AccountDTO }>(
      `/v1/accounts/${accountId}/disable_bill_holds`,
    );
    return data.account;
  }

  async createMembership(
    accountId: string,
    membership: Partial<MembershipDTO>,
  ): Promise<MembershipDTO> {
    const { data } = await this.httpPost<{ membership: MembershipDTO }>(
      `/v1/accounts/${accountId}/memberships`,
      { data: { membership } },
    );
    return data.membership;
  }

  async deleteMembership(accountId: string, membershipId: string): Promise<void> {
    await this.httpDelete(`/v1/accounts/${accountId}/memberships/${membershipId}`);
  }

  async resendInvite(userId: string): Promise<void> {
    await this.httpPost(`/v1/users/${userId}/resend_invite`);
  }

  async getCurrentUser(options?: QueryRequestOptions): Promise<UserDTO> {
    const { data } = await this.httpGet('/v1/users/me', options);
    return {
      ...data.user,
      memberships: await this.userMembershipSorter.getUserMembership(data.user),
    };
  }

  async setUserActiveMembership(activeMembershipId: string): Promise<UserDTO> {
    const { data } = await this.httpPatch<{ user: UserDTO }>('/v1/users/me', {
      data: { user: { activeMembershipId } },
    });
    return {
      ...data.user,
      memberships: await this.userMembershipSorter.updateMembershipLastAccessedAt(
        data.user,
        activeMembershipId,
      ),
    };
  }

  async getLocation(id: string, options?: QueryRequestOptions): Promise<LocationDTO> {
    const { data } = await this.httpGet(`/v1/locations/${id}`, options);
    return data.location;
  }

  async getLocations(
    query?: {
      take?: number;
      cursor?: string;
      accountId?: string;
      search?: string;
      locationIds?: string[];
      status?: LocationStatus;
      sortBy?: LocationSortBy;
      sortDirection?: SortDirection;
    },
    options?: QueryRequestOptions,
  ): Promise<PaginatedResponse<LocationDTO>> {
    const { data } = await this.httpGet('/v1/locations', {
      params: {
        take: query?.take,
        cursor: query?.cursor,
        accountId: query?.accountId,
        status: query?.status,
        locationIds: query?.locationIds?.join(','),
        q: query?.search,
        sortBy: query?.sortBy ? snakeCase(query.sortBy) : undefined,
        sortDirection: query?.sortDirection,
      },
      ...options,
    });
    return { ...data, items: data.locations };
  }

  async createLocation(location: Partial<LocationDTO>): Promise<LocationDTO> {
    const { data } = await this.httpPost<{ location: LocationDTO }>('/v1/locations', {
      data: { location },
    });
    return data.location;
  }

  async getBills(
    query?: {
      take?: number;
      cursor?: string;
      search?: string;
      status?: BillStatus;
      accountId?: string;
      sortBy?: BillSortBy;
      sortDirection?: SortDirection;
    },
    options?: QueryRequestOptions,
  ): Promise<PaginatedResponse<BillDTO>> {
    const { data } = await this.httpGet('/v1/bills', {
      params: {
        take: query?.take,
        cursor: query?.cursor,
        q: query?.search,
        status: query?.status,
        accountId: query?.accountId,
        sortBy: query?.sortBy ? snakeCase(query.sortBy) : undefined,
        sortDirection: query?.sortDirection,
      },
      ...options,
    });
    return { ...data, items: data.bills };
  }

  async getBill(id: string, options?: QueryRequestOptions): Promise<BillDTO> {
    const { data } = await this.httpGet(`/v1/bills/${id}`, options);
    return data.bill;
  }

  async closeBill(billId: string): Promise<void> {
    await this.httpPost(`/v1/bills/${billId}/close`);
  }

  async recalculateBill(billId: string): Promise<void> {
    await this.httpPost(`/v1/bills/${billId}/recalculate`);
  }

  async addBillHold(billId: string, reason: string): Promise<BillDTO> {
    const { data } = await this.httpPost<{ bill: BillDTO }>(`/v1/bills/${billId}/holds`, {
      data: { reason },
    });
    return data.bill;
  }

  async releaseBillHold(billId: string, holdId: string): Promise<BillDTO> {
    const { data } = await this.httpDelete<{ bill: BillDTO }>(
      `/v1/bills/${billId}/holds/${holdId}`,
    );
    return data.bill;
  }

  async queryBills(
    input: QueryBillsProps,
    options: { signal?: RequestOptions['signal'] },
  ): Promise<BillsQueryQuery> {
    // Ensure limit is always a positive number
    const limit = Math.max(1, Math.abs(input.take ?? DEFAULT_PAGE_SIZE));

    const whereClause: BillsBoolExp[] = [];

    if (input.search) {
      const searchPattern = `%${input.search}%`;
      whereClause.push({
        _or: [
          { name: { _ilike: searchPattern } },
          { account: { name: { _ilike: searchPattern } } },
          { location: { name: { _ilike: searchPattern } } },
        ],
      });
    }

    const { filters } = input;

    if (filters?.status && filters.status.value.length > 0) {
      whereClause.push({
        status: { [graphqlOperatorMap[filters.status.operator]]: filters.status.value },
      });
    }
    if (filters?.accountName && filters.accountName.value.length > 0) {
      whereClause.push(getBillRelationNameWhereClause('account', filters.accountName));
    }
    if (filters?.locationName && filters.locationName.value.length > 0) {
      whereClause.push(getBillRelationNameWhereClause('location', filters.locationName));
    }
    if (filters?.billTotal) {
      whereClause.push({
        billTotal: { [graphqlOperatorMap[filters.billTotal.operator]]: filters.billTotal.value },
      });
    }
    if (filters?.hasHolds) {
      // `{ holds: {} }` is the DDN existence predicate — "has >= 1 related hold".
      whereClause.push(filters.hasHolds.value ? { holds: {} } : { _not: { holds: {} } });
    }

    /**
     * Sorting
     */
    const orderBy = input.sortBy
      ? input.sortBy.map(({ key, direction }) =>
          getBillOrderByClause(key, direction === 'desc' ? OrderBy.Desc : OrderBy.Asc),
        )
      : [];

    const variables: BillsQueryQueryVariables = {
      limit,
      offset: input.offset ?? 0,
      where: whereClause.length > 0 ? { _and: whereClause } : {},
      orderBy,
    };

    return this.graphqlQuery<BillsQueryQuery, BillsQueryQueryVariables>(
      billsQuery,
      variables,
      options.signal,
    );
  }

  async createServiceAgreement(
    serviceAgreement: Partial<ServiceAgreementDTO>,
  ): Promise<ServiceAgreementDTO> {
    const { data } = await this.httpPost<{ serviceAgreement: ServiceAgreementDTO }>(
      '/v1/service_agreements',
      {
        data: { serviceAgreement },
      },
    );
    return data.serviceAgreement;
  }

  async setServiceAgreementCommencement(
    serviceAgreementId: string,
    serviceAgreement: { commencementDate: string },
  ): Promise<ServiceAgreementDTO> {
    const { data } = await this.httpPatch<{ serviceAgreement: ServiceAgreementDTO }>(
      `/v1/service_agreements/${serviceAgreementId}`,
      {
        data: { serviceAgreement },
      },
    );
    return data.serviceAgreement;
  }

  async terminateServiceAgreement(
    serviceAgreementId: string,
    serviceAgreement: { terminationDate: string },
  ): Promise<ServiceAgreementDTO> {
    const { data } = await this.httpPost<{ serviceAgreement: ServiceAgreementDTO }>(
      `/v1/service_agreements/${serviceAgreementId}/terminate`,
      {
        data: { serviceAgreement },
      },
    );
    return data.serviceAgreement;
  }

  async getListViews(
    query?: {
      cursor?: string;
      take?: number;
      listId?: string;
    },
    options?: QueryRequestOptions,
  ): Promise<PaginatedResponse<ListViewDTO>> {
    const { data } = await this.httpGet('/v1/list_views', {
      params: {
        cursor: query?.cursor,
        take: query?.take,
        listId: query?.listId,
      },
      ...options,
    });
    return { ...data, items: data.listViews };
  }

  async createListView(listView: CreateListViewDTO): Promise<ListViewDTO> {
    const { data } = await this.httpPost<{ listView: ListViewDTO }>('/v1/list_views', {
      data: { listView },
    });
    return data.listView;
  }

  async updateListView(listViewId: string, listView: UpdateListViewDTO): Promise<ListViewDTO> {
    const { data } = await this.httpPatch<{ listView: ListViewDTO }>(
      `/v1/list_views/${listViewId}`,
      { data: { listView } },
    );
    return data.listView;
  }

  async deleteListView(listViewId: string): Promise<void> {
    await this.httpDelete(`/v1/list_views/${listViewId}`);
  }
}
